// Pulls booking details out of pasted text using plain pattern matching.
// Runs entirely in the browser — no API, no key, no cost, works offline.
//
// Phone numbers are the reliable win; dates, guest count and a labelled name
// are picked up when they're unambiguous. Anything it isn't sure about is
// left alone for Jeremy to fill in.

// Australian numbers are 0 + area digit (2,3,4,7,8) + 8 digits, optionally
// written as +61 with the leading 0 dropped, and split by spaces/dots/dashes
// or wrapped in parens.
const PHONE_RE = /(?:\+?61[ .\-()]*|\b0)[23478](?:[ .\-()]*\d){8}\b/g;

export function extractPhone(text) {
  for (const match of text.match(PHONE_RE) || []) {
    const digits = match.replace(/[^\d]/g, "");
    // Strip a +61 country code back to the national 0-prefixed form.
    const national = digits.startsWith("61") && digits.length === 11 ? `0${digits.slice(2)}` : digits;
    if (/^0[23478]\d{8}$/.test(national)) return `+61${national.slice(1)}`;
  }
  return null;
}

const MONTHS = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12,
};

const MONTH_NAMES = Object.keys(MONTHS).join("|");
// "20 Sep", "20 September 2026", "Sep 20", "2026-09-20", "20/09/2026"
const DATE_RE = new RegExp(
  String.raw`\b(?:(\d{4})-(\d{2})-(\d{2})` +
    String.raw`|(\d{1,2})\s*(?:st|nd|rd|th)?\s+(${MONTH_NAMES})[a-z]*\.?(?:\s+(\d{4}))?` +
    String.raw`|(${MONTH_NAMES})[a-z]*\.?\s+(\d{1,2})(?:\s*(?:st|nd|rd|th))?(?:,?\s+(\d{4}))?` +
    String.raw`|(\d{1,2})/(\d{1,2})/(\d{4}))\b`,
  "gi"
);

function toIso(year, month, day) {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const iso = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const parsed = new Date(`${iso}T00:00:00`);
  // Reject dates the calendar doesn't actually have (e.g. 31 Feb).
  return parsed.getDate() === day && parsed.getMonth() + 1 === month ? iso : null;
}

// A year-less date means the nearest sensible one: this year if it hasn't long
// passed, otherwise next year — matching how people write "arriving 20 Sep".
function resolveYear(month, day) {
  const today = new Date();
  const thisYear = toIso(today.getFullYear(), month, day);
  if (!thisYear) return null;
  const daysAway = (new Date(`${thisYear}T00:00:00`) - today) / 86_400_000;
  return daysAway >= -14 ? thisYear : toIso(today.getFullYear() + 1, month, day);
}

export function extractDates(text) {
  const found = [];
  for (const m of text.matchAll(DATE_RE)) {
    let iso = null;
    if (m[1]) {
      iso = toIso(Number(m[1]), Number(m[2]), Number(m[3]));
    } else if (m[4]) {
      const month = MONTHS[m[5].toLowerCase()];
      iso = m[6] ? toIso(Number(m[6]), month, Number(m[4])) : resolveYear(month, Number(m[4]));
    } else if (m[7]) {
      const month = MONTHS[m[7].toLowerCase()];
      iso = m[9] ? toIso(Number(m[9]), month, Number(m[8])) : resolveYear(month, Number(m[8]));
    } else if (m[10]) {
      // Day-first, per Australian convention.
      iso = toIso(Number(m[12]), Number(m[11]), Number(m[10]));
    }
    if (iso && !found.includes(iso)) found.push(iso);
  }

  found.sort();
  return { starts_on: found[0] || null, ends_on: found[1] || null };
}

export function extractGuestCount(text) {
  const m =
    text.match(/\b(\d{1,2})\s*(?:adults?\s*(?:\+|and)?\s*\d*\s*(?:kids?|children)?\s*)?(?:guests?|people|pax|adults?)\b/i) ||
    text.match(/\b(?:guests?|party\s+size|number\s+of\s+guests)\s*[:\-]\s*(\d{1,2})\b/i);
  const count = m ? Number(m[1]) : null;
  return count && count > 0 && count <= 30 ? count : null;
}

// Name extraction, most-reliable signal first:
//   1. an explicit label ("Guest: …", "Name: …", "Booked by …")
//   2. a self-introduction ("I'm X", "my name is X", "this is X")
//   3. a sign-off at the end ("Thanks, X" / "Cheers, X" / "– X")
//   4. best guess: the first capitalised First Last pair that isn't a known
//      non-name (places, platforms, months, greetings…)
const NAME_LABEL_RE = /\b(?:guest name|guest|name|booked by|reserved by)\s*[:\-]\s*(.{1,60})/i;
// The label parts match case-insensitively; the captured name is re-checked
// by plausibleName (case-sensitive), so lowercase captures get rejected there.
const INTRO_RE = /\b(?:i['’]m|i am|my name(?:['’]s| is)|this is)\s+([\p{L}'’-]+(?:\s+[\p{L}'’-]+)?)/iu;
const SIGNOFF_RE = /(?:thanks|thank you|cheers|regards|kind regards|best|talk soon)[,!.]?\s*\n?\s*[-–—]?\s*([\p{L}'’-]+(?:\s+[\p{L}'’-]+)?)\s*$/imu;
const LOOKS_LIKE_NAME_RE = /^[A-Z][\p{L}'’-]*(?:\s+[A-Z][\p{L}'’-]*){0,2}$/u;
// Each word must start Capital-then-lowercase so "I'm Marcus" isn't a "pair".
const CAP_PAIR_RE = /\b([A-Z][a-z][\p{L}'’-]*)\s+([A-Z][a-z][\p{L}'’-]*)\b/gu;

// Words that look like names but never are, in this app's messages.
const NOT_NAMES = new Set([
  "jacky", "winter", "gardens", "waters", "places", "airbnb", "riparide", "whatsapp",
  "belgrave", "walkerville", "melbourne", "victoria",
  "hi", "hello", "hey", "dear", "thanks", "thank", "cheers", "regards", "best", "kind",
  "guest", "guests", "booking", "request", "confirmation", "check", "reservation",
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
  "january", "february", "march", "april", "may", "june", "july", "august",
  "september", "october", "november", "december", "jan", "feb", "mar", "apr",
  "jun", "jul", "aug", "sep", "sept", "oct", "nov", "dec",
]);

function plausibleName(candidate) {
  if (!candidate || !LOOKS_LIKE_NAME_RE.test(candidate)) return null;
  const words = candidate.split(/\s+/);
  if (words.some((w) => NOT_NAMES.has(w.toLowerCase()))) return null;
  return candidate;
}

export function extractName(text) {
  const labelled = text.match(NAME_LABEL_RE);
  if (labelled) {
    // Cut at whatever follows the name: punctuation, or a spaced dash.
    const candidate = labelled[1].split(/[,;\n\r|(]|\s[—–-]\s/)[0].trim().replace(/\s+/g, " ");
    const name = plausibleName(candidate);
    if (name) return name;
  }

  const intro = text.match(INTRO_RE);
  if (intro && plausibleName(intro[1])) return intro[1];

  const signoff = text.match(SIGNOFF_RE);
  if (signoff && plausibleName(signoff[1])) return signoff[1];

  // Last resort: first plausible First Last pair anywhere, skipping pairs
  // that directly follow a greeting (that's whoever the message is *to*).
  for (const m of text.matchAll(CAP_PAIR_RE)) {
    const before = text.slice(Math.max(0, m.index - 12), m.index);
    if (/\b(?:hi|hey|hello|dear)[,!]?\s*$/i.test(before)) continue;
    const name = plausibleName(`${m[1]} ${m[2]}`);
    if (name) return name;
  }
  return null;
}

export function extractDetails(text) {
  const { starts_on, ends_on } = extractDates(text);
  const fields = {
    guest_name: extractName(text),
    guest_phone: extractPhone(text),
    guest_count: extractGuestCount(text),
    starts_on,
    ends_on,
  };
  // Drop anything not found so callers can spread the result safely.
  return Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== null));
}
